<template>
  <div class="product-detail-page">
    <main-header />

    <!-- Hero Section with Product Gallery and Info -->
    <section class="hero-section">
      <div class="container">
        <div class="product-grid">
          <!-- Product Gallery -->
          <div class="gallery-section">
            <div class="gallery-nav">
              <button 
                v-for="(img, index) in productImages" 
                :key="index"
                :class="['gallery-thumb', { active: selectedImageIndex === index }]"
                @click="selectedImageIndex = index"
              >
                <img :src="img" :alt="`${currentSetName} thumbnail ${index + 1}`" />
              </button>
            </div>
            <div class="main-image-container">
              <img :src="currentMainImage" :alt="`${currentSetName} Gift Box main image`" />
            </div>
          </div>

          <!-- Product Information -->
          <div class="product-info">
            <div class="product-header">
              <div class="rating-section">
                <div class="stars">
                  <span v-for="n in 5" :key="n" class="star filled">★</span>
                </div>
                <span class="rating-value">5</span>
                <!-- <span class="review-count">(20 Reviews)</span> -->
              </div>
              <!-- <div class="discount-badge">25% OFF</div> -->
            </div>

            <h1 class="product-title">{{ currentSetName }} Gift Box</h1>
            
            <p class="product-description">
              {{ currentSetDescription }}
            </p>

            <div class="price-section">
              <span class="current-price">${{ currentPrice.toFixed(2) }}</span>
              <span class="original-price" v-if="currentOriginalPrice > currentPrice">${{ currentOriginalPrice.toFixed(2) }}</span>
              <span class="stock-status" v-if="isProductInStock">In Stock</span>
              <span class="stock-status" v-else style="color: #989898;">Out of Stock</span>
            </div>

            <div class="shipping-info">
              <p>Free Standard Shipping Over $49.00</p>
              <p>Shipping Time: 7 - 10 days</p>
              <p class="production-time">Production: 5-8 days</p>
            </div>

            <button class="cta-button" @click="startPersonalization">
              <span>START YOUR</span>
              <span>PERSONALIZATION</span>
              <span>JOURNEY INSTANTLY</span>
              <svg class="arrow-icon" width="20" height="20" viewBox="0 0 20 20">
                <path d="M5 10h10M10 5l5 5-5 5" stroke="currentColor" stroke-width="2" fill="none"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Journey Features Section -->
    <section class="journey-section">
      <div class="container">
        <div class="journey-features">
          <div
            class="feature-card"
            v-for="(feature, index) in journeyFeatures"
            :key="'journey-' + index"
          >
            <!-- <div class="feature-icon">{{ feature.icon }}</div> -->
            <h3>{{ feature.title }}</h3>
            <p>{{ feature.subtitle }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Process Timeline Section -->
    <section class="process-section">
      <div class="container">
        <h2 class="section-title">Explore Our Personalization Process</h2>
        <p class="section-subtitle">YOU Personalize, WE Deliver</p>
        
        <div class="process-timeline">
          <div class="timeline-line"></div>
          
          <div class="process-step" v-for="(step, index) in processSteps" :key="index">
            <div class="step-number">{{ (index + 1).toString().padStart(2, '0') }}</div>
            <div class="step-icon">{{ step.icon }}</div>
            <h3>{{ step.title }}</h3>
            <p>{{ step.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Product Items Showcase - Figma: three-column layout -->
    <section class="product-showcase-section">
      <div class="container">
        <div class="showcase-header" :style="showcaseHeaderStyle">
          <div class="showcase-header__overlay">
            <button class="tab-button active">Product Description</button>
          </div>
        </div>

        <div class="three-col-layout">
          <!-- Left expanding cards -->
          <div class="side-column left">
            <div 
              class="info-card"
              v-for="(item, index) in leftItems" 
              :key="'left-' + index"
              :class="{ open: leftActiveIndex === index }"
              @mouseenter="setLeftActive(index)"
              @focusin="setLeftActive(index)"
              @click="setLeftActive(index)"
            >
              <div class="card-header">
                <span class="idx">({{ index + 1 }})</span>
                <span class="name">{{ item.name }}</span>
              </div>
              <div class="card-body">
                <template v-if="item.productImage">
                  <img
                    :src="item.productImage"
                    :alt="`${item.name} preview`"
                    class="card-body-image"
                  />
                </template>
                <p v-else>{{ item.description }}</p>
              </div>
            </div>
          </div>

          <!-- Center image -->
          <div class="center-media">
            <div class="center-image">
              <img :src="currentLabeledImage" :alt="`${currentSetName} Gift Box labeled layout`" />
            </div>
          </div>

          <!-- Mobile swiper for cards -->
          <div class="mobile-swiper">
            <div class="swiper-track">
              <div class="swiper-slide" v-for="(item, index) in currentGiftBoxItems" :key="'m-' + index">
                <div class="info-card">
                  <div class="card-header">
                    <span class="idx">({{ index + 1 }})</span>
                    <span class="name">{{ item.name }}</span>
                  </div>
                  <div class="card-body mobile-expanded">
                    <template v-if="item.productImage">
                      <img
                        :src="item.productImage"
                        :alt="`${item.name} preview`"
                        class="card-body-image"
                      />
                    </template>
                    <p v-else>{{ item.description }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Right expanding cards -->
          <div class="side-column right">
            <div 
              class="info-card"
              v-for="(item, idx) in rightItems" 
              :key="'right-' + idx"
              :class="{ open: rightActiveIndex === idx }"
              @mouseenter="setRightActive(idx)"
              @focusin="setRightActive(idx)"
              @click="setRightActive(idx)"
            >
              <div class="card-header">
                <span class="idx">({{ idx + leftItems.length + 1 }})</span>
                <span class="name">{{ item.name }}</span>
              </div>
              <div class="card-body">
                <template v-if="item.productImage">
                  <img
                    :src="item.productImage"
                    :alt="`${item.name} preview`"
                    class="card-body-image"
                  />
                </template>
                <p v-else>{{ item.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="no-background-section" v-if="noBackgroundItems.length">
      <div class="container">
        <div class="no-background-grid">
          <div
            class="no-background-card"
            v-for="(item, index) in noBackgroundItems"
            :key="'no-background-' + index"
          >
            <span class="no-background-index">({{ index + 1 }})</span>
            <div class="no-background-image-wrapper">
              <div class="no-background-circle"></div>
              <img
                :src="item.image"
                :alt="`${item.name} product render without background`"
              />
            </div>
            <p class="no-background-name">{{ item.name }}</p>
          </div>
        </div>
      </div>
    </section>

    <section class="travel-section" v-if="travelImage">
      <div class="container travel-container">
        <div class="travel-copy">
          <h3 class="travel-title">Travel with {{ currentSetName }}</h3>
          <p class="travel-subtitle">{{ travelSubtitle }}</p>
        </div>
        <div class="travel-visual">
          <div class="travel-polaroid">
            <img :src="travelImage" :alt="`Travel with ${currentSetName}`" />
          </div>
        </div>
      </div>
    </section>

    <!-- Reviews, Q&A, and Testimonials Section -->
    <!-- <ReviewsQASection :reviews="reviews" :questions="questions" :testimonials="testimonials" /> -->

    <!-- Call to Action Section -->
    <section class="cta-section">
      <div class="container cta-container">
        <div class="cta-visual">
          <img src="@/assets/journey/oxytocin@2x.png" alt="Oxytocin Molecule" />
        </div>
        <div class="cta-content">
          <h3 class="cta-subtitle-small">{{ currentSetName }} GIFT BOX</h3>
          <h2 class="cta-title">
            YOUR STORY,<br>
            YOUR PET,<br>
            YOUR {{ currentSetName.toUpperCase() }}.
          </h2>
        </div>
      </div>
    </section>

    <!-- Animal Portrait Upgrade Banner -->
    <portrait-upgrade-banner />

    <main-footer />
  </div>
</template>

<script>
import MainHeader from "@/components/MainHeader.vue";
import MainFooter from "@/components/MainFooter.vue";
import ReviewsQASection from "@/components/ReviewsQASection.vue";
import setDefinitions from "@/../public/data/personalization/sets2.json";
import productsData from "@/../public/data/products.json";
import PortraitUpgradeBanner from '@/components/product/PortraitUpgradeBanner.vue';

const DEFAULT_SET_KEY = "Accompania";

const setEntries = Object.entries(setDefinitions).map(([key, value]) => ({
  key,
  id: value && value._id ? value._id.toString() : key,
  data: value
}));

const DEFAULT_SET_ENTRY =
  setEntries.find((entry) => entry.key === DEFAULT_SET_KEY) ||
  setEntries[0] ||
  null;

function resolveSetEntry(identifier) {
  if (!setEntries.length) {
    return null;
  }

  if (!identifier) {
    return DEFAULT_SET_ENTRY || setEntries[0];
  }

  const normalized = identifier.toString();

  let entry =
    setEntries.find((item) => item.id === normalized) || null;

  if (entry) {
    return entry;
  }

  entry =
    setEntries.find(
      (item) => item.key.toLowerCase() === normalized.toLowerCase()
    ) || null;

  return entry || DEFAULT_SET_ENTRY || setEntries[0];
}

export default {
  name: "SetView",
  components: { MainHeader, MainFooter, ReviewsQASection, PortraitUpgradeBanner },
  data() {
    return {
      selectedImageIndex: 0,
      currentSetIdentifier: DEFAULT_SET_ENTRY ? DEFAULT_SET_ENTRY.id : DEFAULT_SET_KEY,
      processSteps: [
        {
          title: 'CHOOSE A GIFT BOX',
          icon: '🎁',
          description: 'Choose a customizable gift box (Accompania, Everlast, Artistico)'
        },
        {
          title: 'SELECT YOUR JOURNEY',
          icon: '🐾',
          description: 'Loving, Gifting, Memorial, Away From Home, Wild Animal Love'
        },
        {
          title: 'Customization!',
          icon: '✏️',
          description: 'Customize with your preferred level of control'
        },
        {
          title: 'ADD to cart',
          icon: '🛒',
          description: 'Add your unique gift box in the shopping cart'
        },
        {
          title: 'Pay',
          icon: '💳',
          description: 'You\'re almost there! Get the payment done and the gift box will be ready soon'
        },
        {
          title: 'Get delivered',
          icon: '📦',
          description: 'Let\'s open a box of happy! Your unique gift box will be delivered to your doorstep'
        }
      ],
      reviews: [
        {
          id: 1,
          title: 'Adorable and Unique',
          username: '_FerretLover_91',
          date: 'June 14, 2025',
          text: "I never thought I'd find something this cute for a ferret! Everything was perfectly sized and personal.",
          petName: 'Noodle',
          petType: 'Ferret',
          rating: 5
        },
        {
          id: 2,
          title: 'So Thoughtful and Sweet',
          username: 'FeatheredSoul',
          date: 'May 21, 2025',
          text: "I was surprised they supported birds! Kiwi's colors were matched perfectly on the keychain—totally worth it.",
          petName: 'Kiwi',
          petType: 'Parrot',
          rating: 5
        },
        {
          id: 3,
          title: 'Perfect Gift for Cat Moms',
          username: 'WhiskerQueen',
          date: 'May 16, 2025',
          text: "I loved customizing every item for Mochi. The Minimi figurine looks just like her curled-up pose!",
          petName: 'Mochi',
          petType: 'Tabby Cat',
          rating: 5
        }
      ],
      questions: [
        {
          question: 'Can I customize the products with my own pet?',
          answer: 'Yes! You can upload your pet\'s photo and select features to create custom designs for each item.'
        },
        {
          question: 'Which types of pets can be customized?',
          answer: 'We support dogs, cats, rabbits, birds, and even small pets like ferrets and hedgehogs.'
        },
        {
          question: 'How long does customization and delivery take?',
          answer: 'Customization takes 10-25 mins to complete. Shipping typically adds 7–14 days depending on your location.'
        },
        {
          question: 'What if I have more than one pet?',
          answer: 'Currently, each box supports one pet per order. For multiple pets, you can place separate orders.'
        },
        {
          question: 'Is this a good gift for someone else?',
          answer: 'Absolutely! It\'s a thoughtful, personalized gift for any pet lover.'
        }
      ],
      testimonials: [
        {
          id: 1,
          text: "Got this as a surprise from a friend after losing my pup. It's comforting, personal, and full of love.",
          petName: 'Bucky',
          petInfo: 'British Bulldog, Age 2',
          location: 'Charleston, SC'
        },
        {
          id: 2,
          text: "The customization blew me away. The figurine looks just like my cat, whiskers and all.",
          petName: 'Kona',
          petInfo: 'Tabby Cat, Age 5',
          location: 'Seattle, WA'
        },
        {
          id: 3,
          text: "I cried happy tears opening this. Every item felt like a love letter to my dog.",
          petName: 'Remi',
          petInfo: 'Golden Retriever, Age 4',
          location: 'San Diego, CA'
        }
      ],
      leftActiveIndex: 0,
      rightActiveIndex: 0
    };
  },
  computed: {
    currentSetEntry() {
      return resolveSetEntry(this.currentSetIdentifier);
    },
    currentSet() {
      return this.currentSetEntry ? this.currentSetEntry.data : null;
    },
    productImages() {
      return (this.currentSet && Array.isArray(this.currentSet.images))
        ? this.currentSet.images.slice(1, 7)
        : [];
    },
    currentMainImage() {
      if (!this.productImages.length) {
        return "";
      }
      return this.productImages[this.selectedImageIndex] || this.productImages[0];
    },
    currentLabeledImage() {
      const labeledImages = (this.currentSet && Array.isArray(this.currentSet.labeled_images))
        ? this.currentSet.labeled_images
        : [];
      if (labeledImages.length) {
        return labeledImages[0];
      }
      return this.currentMainImage;
    },
    currentSetName() {
      if (this.currentSet && this.currentSet.name) {
        return this.currentSet.name;
      }
      if (this.currentSetEntry && this.currentSetEntry.key) {
        return this.currentSetEntry.key;
      }
      return DEFAULT_SET_KEY;
    },
    currentSetDescription() {
      if (this.currentSet && this.currentSet.description) {
        return this.currentSet.description;
      }
      return "This thoughtfully curated collection celebrates the bond between pets and their humans through personalized keepsakes.";
    },
    currentGiftBoxItems() {
      if (this.currentSet && Array.isArray(this.currentSet.products)) {
        const sortedProducts = [...this.currentSet.products].sort((a, b) => {
          const orderA = typeof a.display_order === 'number' ? a.display_order : Number.MAX_SAFE_INTEGER;
          const orderB = typeof b.display_order === 'number' ? b.display_order : Number.MAX_SAFE_INTEGER;
          return orderA - orderB;
        });

        return sortedProducts.map((product, index) => ({
          name: product.name || `Item ${index + 1}`,
          productImage: product.product_image || '',
          description: product.description || ''
        }));
      }
      return [];
    },
    currentNoBackgroundImages() {
      return (this.currentSet && Array.isArray(this.currentSet.no_background_images))
        ? this.currentSet.no_background_images
        : [];
    },
    noBackgroundItems() {
      const images = this.currentNoBackgroundImages;
      if (!images.length) {
        return [];
      }
      const giftItems = this.currentGiftBoxItems;
      return images.map((image, index) => ({
        image,
        name: giftItems[index] ? giftItems[index].name : `Item ${index + 1}`
      }));
    },
    travelImage() {
      if (!this.currentSet || !this.currentSet.travel_image) {
        return "";
      }
      const image = this.currentSet.travel_image.trim();
      if (!image) {
        return "";
      }
      return image.startsWith("/") ? image : `/${image.replace(/^\/+/, "")}`;
    },
    travelSubtitle() {
      if (this.currentSet && this.currentSet.travel_subtitle) {
        return this.currentSet.travel_subtitle;
      }
      return "A Gift Box That Travels Through Life With You and Your Pet";
    },
    journeyFeatures() {
      const defaults = [
        {
          icon: '🐾',
          title: 'A Journey of Togetherness',
          subtitle: 'Named for lifelong companionship, celebrating the journey pets and humans share together.'
        },
        {
          icon: '🎁',
          title: 'Six Keepsakes, One Meaningful Box',
          subtitle: 'Includes six curated items that capture the love and memories shared with your pet.'
        },
        {
          icon: '✨',
          title: 'Fully Customizable Experience',
          subtitle: 'Personalize each item with your pet\'s unique features to create a truly one-of-a-kind gift.'
        }
      ];

      const slogans = [
        this.currentSet && this.currentSet.slogan_1,
        this.currentSet && this.currentSet.slogan_2,
        this.currentSet && this.currentSet.slogan_3
      ];

      return defaults.map((item, index) => {
        const slogan = slogans[index] || {};
        return {
          icon: item.icon,
          title: slogan.title || item.title,
          subtitle: slogan.subtitle || item.subtitle
        };
      });
    },
    showcaseBackgroundImage() {
      const allImages = (this.currentSet && Array.isArray(this.currentSet.images))
        ? this.currentSet.images
        : [];
      return allImages[7] || allImages[0] || this.currentMainImage;
    },
    showcaseHeaderStyle() {
      return this.showcaseBackgroundImage
        ? {
            backgroundImage: `url(${this.showcaseBackgroundImage})`
          }
        : {};
    },
    leftItems() {
      const half = Math.ceil(this.currentGiftBoxItems.length / 2);
      return this.currentGiftBoxItems.slice(0, half);
    },
    rightItems() {
      const half = Math.ceil(this.currentGiftBoxItems.length / 2);
      return this.currentGiftBoxItems.slice(half);
    },
    currentProductInfo() {
      if (!productsData || !productsData.results) return null;
      const name = this.currentSetName;
      return productsData.results.find(p => p.name.toLowerCase() === name.toLowerCase());
    },
    currentPrice() {
      return this.currentProductInfo ? this.currentProductInfo.price : 0;
    },
    currentOriginalPrice() {
      return this.currentProductInfo ? this.currentProductInfo.original_price : 0;
    },
    isProductInStock() {
      return this.currentProductInfo ? this.currentProductInfo.in_stock : true;
    }
  },
  watch: {
    '$route.params.id': {
      handler() {
        this.syncCurrentSetFromRoute();
      },
      immediate: false
    },
    '$route.query.set': {
      handler() {
        this.syncCurrentSetFromRoute();
      },
      immediate: false
    },
    productImages(newImages) {
      if (!newImages || !newImages.length) {
        this.selectedImageIndex = 0;
        return;
      }
      if (this.selectedImageIndex >= newImages.length) {
        this.selectedImageIndex = 0;
      }
    },
    currentGiftBoxItems(newItems) {
      const leftLength = Math.ceil(newItems.length / 2) || 0;
      const rightLength = newItems.length - leftLength;
      if (this.leftActiveIndex >= leftLength) {
        this.leftActiveIndex = 0;
      }
      if (this.rightActiveIndex >= rightLength) {
        this.rightActiveIndex = 0;
      }
    }
  },
  created() {
    this.syncCurrentSetFromRoute();
  },
  methods: {
    startPersonalization() {
      this.$router.push('/personalization/'+this.currentSetIdentifier);
    },
    setLeftActive(index) {
      this.leftActiveIndex = index;
    },
    setRightActive(index) {
      this.rightActiveIndex = index;
    },
    syncCurrentSetFromRoute() {
      const routeId = this.$route.params.id;
      const queryKey = this.$route.query && this.$route.query.set;
      const incomingIdentifier = routeId || queryKey;
      this.currentSetIdentifier = incomingIdentifier
        ? incomingIdentifier.toString()
        : (DEFAULT_SET_ENTRY ? DEFAULT_SET_ENTRY.id : DEFAULT_SET_KEY);
      this.selectedImageIndex = 0;
      this.leftActiveIndex = 0;
      this.rightActiveIndex = 0;
    }
  }
};
</script>

<style scoped>
/* Base Styles */
.product-detail-page {
  background: #fffef9;
  min-height: 100vh;
  font-family: 'Jost', sans-serif;
  overflow-x: hidden; /* prevent horizontal scroll */
}

.container {
  max-width: 1440px;
  margin: 0 auto;
  padding: 0 60px;
  width: 100%;
  box-sizing: border-box;
}

/* Hero Section */
.hero-section {
  padding: 40px 0 60px;
  background: #fffef9;
}

.product-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 80px;
  align-items: start;
}

/* Prevent children from expanding grid width due to intrinsic min-width:auto */
.product-grid > * {
  min-width: 0;
}

/* Gallery */
.gallery-section {
  display: flex;
  gap: 20px;
}

/* Ensure flex container can shrink inside grid cell */
.gallery-section,
.product-info {
  min-width: 0;
}

.gallery-nav {
  display: flex;
  flex-direction: column;
  gap: 10px;
  /* Prevent nav from expanding the layout on desktop */
  width: 100px;
  flex: 0 0 100px;
  max-width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 527px;
}

.gallery-thumb {
  width: 100px;
  height: 54px;
  border: 2px solid transparent;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  background: white;
  padding: 0;
  transition: border-color 0.3s;
}

.gallery-thumb.active {
  border-color: #d78d86;
}

.gallery-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.main-image-container {
  flex: 1;
  border-radius: 10px;
  overflow: hidden;
  height: auto;
  aspect-ratio: 1 / 1; /* responsive square that shrinks with width */
  max-height: 527px; /* cap for large screens */
}

.main-image-container img {
  width: 100%;
  height: 100%;
  object-fit: cover; /* fill square frame and crop overflow */
  object-position: center;
}

/* Product Info */
.product-info {
  padding-top: 20px;
}

.product-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.rating-section {
  display: flex;
  align-items: center;
  gap: 8px;
}

.stars {
  display: flex;
  gap: 2px;
}

.star {
  color: #e0e0e0;
  font-size: 18px;
}

.star.filled {
  color: #e37267;
}

.rating-value {
  font-weight: 600;
  font-size: 16px;
  color: #000;
}

.review-count {
  color: #000;
  font-size: 16px;
}

.discount-badge {
  background: #ffe9dd;
  color: #e37267;
  padding: 6px 16px;
  border-radius: 4px;
  font-weight: 600;
  font-size: 14px;
}

.product-title {
  font-size: 30px;
  font-weight: 600;
  color: #000;
  margin: 0 0 20px;
  overflow-wrap: anywhere;
}

.product-description {
  color: #989898;
  font-size: 12px;
  line-height: 1.5;
  margin-bottom: 20px;
  overflow-wrap: anywhere;
}

.price-section {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.current-price {
  font-size: 18px;
  font-weight: 600;
  color: #000;
}

.original-price {
  font-size: 14px;
  color: #d78d86;
  text-decoration: line-through;
}

.stock-status {
  margin-left: auto;
  color: #e37267;
  font-weight: 600;
  font-size: 16px;
}

.shipping-info {
  background: #fff1ea;
  padding: 14px 20px;
  border-radius: 8px;
  margin-bottom: 24px;
}

.shipping-info p {
  margin: 0;
  color: #838383;
  font-size: 12px;
  line-height: 1.4;
}

.shipping-info .production-time {
  font-weight: 600;
  color: #e37267;
}

.cta-button {
  width: 257px;
  height: 62px;
  background: white;
  border: 1.5px solid #e37267;
  border-radius: 15px;
  color: #e37267;
  font-weight: 600;
  font-size: 12px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  position: relative;
  transition: all 0.3s ease;
}

.cta-button:hover {
  background: #e37267;
  color: white;
}

.cta-button span {
  display: block;
  line-height: 1.2;
}

.arrow-icon {
  position: absolute;
  right: 20px;
  top: 50%;
  transform: translateY(-50%);
}

/* Journey Section */
.journey-section {
  background: #ffeee5;
  padding: 60px 0;
}

.journey-features {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
}

.feature-card {
  text-align: center;
  background: #fffef9;
  padding: 30px 20px;
  border-radius: 20px;
}

.feature-icon {
  font-size: 48px;
  margin-bottom: 20px;
}

.feature-card h3 {
  font-size: 14px;
  font-weight: 800;
  color: #000;
  margin-bottom: 10px;
}

.feature-card p {
  font-size: 12px;
  color: #989898;
  line-height: 1.5;
}

/* Process Section */
.process-section {
  padding: 80px 0;
  background: #fffef9;
}

.section-title {
  font-size: 24px;
  font-weight: 600;
  color: #e37267;
  text-align: center;
  margin-bottom: 10px;
}

.section-subtitle {
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  color: #000;
  margin-bottom: 60px;
  text-transform: uppercase;
}

.process-timeline {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 30px;
  position: relative;
  padding: 0 20px;
}

.timeline-line {
  position: absolute;
  top: 28px;
  left: 10%;
  right: 10%;
  height: 2px;
  background: linear-gradient(90deg, #e37267 0%, #d78d86 100%);
  z-index: 1;
}

.process-step {
  position: relative;
  z-index: 2;
  text-align: center;
}

.step-number {
  width: 55px;
  height: 55px;
  background: white;
  border: 2px solid #e37267;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  font-size: 24px;
  font-weight: 600;
  color: #000;
}

.step-icon {
  font-size: 48px;
  margin: 20px auto;
}

.process-step h3 {
  font-size: 18px;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 10px;
  color: #000;
}

.process-step p {
  font-size: 12px;
  color: #989898;
  line-height: 1.5;
}

/* Product Showcase Section */
.product-showcase-section {
  background: #fffef9;
  padding: 60px 0;
}

.showcase-header {
  position: relative;
  margin-bottom: 40px;
  border-radius: 20px;
  overflow: hidden;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.showcase-header::before {
  content: "";
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.12);
}

.showcase-header__overlay {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: center;
  width: 100%;
}

.tab-button {
  background: rgba(255, 255, 255, 0.88);
  border: 1.5px solid rgba(255, 255, 255, 0.75);
  color: #e37267;
  padding: 10px 30px;
  border-radius: 10px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
}

.tab-button.active {
  border-color: #e37267;
  color: #e37267;
  box-shadow: 0 8px 24px rgba(227, 114, 103, 0.25);
}

/* Three-column interactive layout */
.three-col-layout {
  display: grid;
  grid-template-columns: 1fr minmax(420px, 560px) 1fr;
  gap: 40px;
  align-items: start;
}

.side-column {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.info-card {
  background: #fffef9;
  border: 2px solid #ffe1cf;
  border-radius: 16px;
  overflow: hidden;
  transition: box-shadow 0.3s ease, transform 0.3s ease;
}

.info-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0,0,0,0.08);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 16px 18px;
  background: #ffe9dd;
  color: #d78d86;
  font-family: 'Eczar', serif;
  font-weight: 800;
  text-transform: uppercase;
}

.card-header .idx { color: #e37267; }
.card-header .name { color: #d78d86; font-size: 14px; }

.card-body {
  padding: 16px 18px;
  opacity: 0;
  max-height: 0;
  transition: opacity 0.3s ease;
}

.info-card:hover .card-body,
.info-card.open .card-body {
  opacity: 1;
  max-height: none;
}

.card-body-image {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 14px;
  background: #fff;
}

.card-body p {
  margin: 12px 0 0 0;
  color: #989898;
  font-size: 12px;
  line-height: 1.5;
}

.card-body-image + p {
  margin-top: 12px;
}

.center-media {
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
}

.center-image {
  width: 100%;
  aspect-ratio: 1/1;
  border-radius: 20px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0px 4px 10px rgba(0,0,0,0.06);
}

.center-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.thumb-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
}

.thumb {
  width: 72px;
  height: 40px;
  border: 2px solid transparent;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  cursor: pointer;
}

.thumb.active { border-color: #d78d86; }
.thumb img { width: 100%; height: 100%; object-fit: cover; }

/* Mobile swiper */
.mobile-swiper { display: none; }
.mobile-swiper .swiper-track {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  padding: 4px 6px 8px;
}
.mobile-swiper .swiper-slide {
  flex: 0 0 85%;
  scroll-snap-align: start;
}

/* No-background Items Section */
.no-background-section {
  background: #fff9f3;
  padding: 80px 0;
}

.no-background-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 32px;
  justify-items: center;
}

.no-background-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
  color: #d78d86;
  --circle-gradient: radial-gradient(circle at 30% 30%, #ffe9d7 0%, #ffd8c8 60%, #fbd9d4 100%);
}

.no-background-card:nth-child(2) {
  --circle-gradient: radial-gradient(circle at 30% 30%, #ffefd6 0%, #ffd9b8 55%, #f5cfc7 100%);
}

.no-background-card:nth-child(3) {
  --circle-gradient: radial-gradient(circle at 30% 30%, #fcebec 0%, #f9d8da 58%, #f5c5cb 100%);
}

.no-background-card:nth-child(4) {
  --circle-gradient: radial-gradient(circle at 30% 30%, #fff1dd 0%, #ffdcbf 56%, #f6d0c0 100%);
}

.no-background-card:nth-child(5) {
  --circle-gradient: radial-gradient(circle at 30% 30%, #fbeff5 0%, #f6dae8 58%, #f3cade 100%);
}

.no-background-index {
  font-family: 'Eczar', serif;
  font-weight: 700;
  font-size: 16px;
  color: #e37267;
  letter-spacing: 0.08em;
}

.no-background-image-wrapper {
  position: relative;
  width: 220px;
  max-width: 100%;
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 26px;
}

.no-background-circle {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: var(--circle-gradient);
  box-shadow: 0 12px 28px rgba(227, 114, 103, 0.12);
  transform: scale(0.85);
  z-index: 0;
}

.no-background-image-wrapper img {
  position: relative;
  z-index: 1;
  width: auto;
  height: 100%;
  transform: scale(1.1);
  object-fit: contain;
}

.no-background-name {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #e37267;
}

.travel-section {
  background: transparent;
  padding: 60px 0;
  position: relative;
  z-index: 2;
  overflow: visible;
}

.travel-section::before {
  content: "";
  position: absolute;
  top: 60%;
  left: 0;
  width: 100%;
  height: 45%;
  background: #ffece4;
  transform: translateY(-50%);
  z-index: -1;
}

.travel-container {
  display: grid;
  grid-template-columns: minmax(220px, 340px) minmax(0, 1fr);
  gap: 60px;
  align-items: center;
}

.travel-copy {
  display: flex;
  flex-direction: column;
  gap: 20px;
  color: #e37267;
  align-self: end;
  margin-bottom: 75px;
}

.travel-title {
  font-family: 'Eczar', serif;
  font-size: 28px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0;
}

.travel-subtitle {
  font-family: 'Jost', sans-serif;
  font-size: 14px;
  line-height: 1.6;
  color: #7d4b40;
  margin: 0;
}

.travel-visual {
  display: flex;
  justify-content: center;
}

.travel-polaroid {
  position: relative;
  width: 120%;
  max-width: none;
  margin-left: -10%;
  /* transform: rotate(-2deg); Optional: add a slight tilt if desired to the whole group */
}

.travel-polaroid img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

/* Reviews and Q&A Section */
.reviews-qa-section {
  background: #fffef9;
  padding: 60px 0;
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 60px;
}

.reviews-header {
  margin-bottom: 30px;
}

.reviews-header h2 {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 20px;
}

.overall-rating {
  background: #f9f9f9;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  margin-bottom: 20px;
}

.stars-large {
  display: flex;
  justify-content: center;
  gap: 4px;
  margin-bottom: 10px;
}

.stars-large .star {
  font-size: 24px;
  color: #e37267;
}

.rating-large {
  font-size: 32px;
  font-weight: 700;
  margin-right: 10px;
}

.based-on {
  font-size: 11px;
  color: #000;
}

.write-review-btn {
  width: 100%;
  padding: 12px;
  background: #e37267;
  color: white;
  border: none;
  border-radius: 5px;
  font-weight: 700;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.3s;
}

.write-review-btn:hover {
  background: #d27b6f;
}

.review-list {
  margin-top: 30px;
}

.review-card {
  border-bottom: 1px solid #f0f0f0;
  padding: 20px 0;
}

.review-header {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
}

.reviewer-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  background: #f0f0f0;
}

.review-info {
  flex: 1;
}

.review-stars {
  display: flex;
  gap: 2px;
  margin-bottom: 5px;
}

.review-info h4 {
  font-size: 14px;
  font-weight: 700;
  margin: 5px 0;
}

.review-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  margin-bottom: 5px;
}

.reviewer-name {
  font-style: italic;
  color: #000;
}

.review-date {
  color: #666;
  font-size: 10px;
}

.review-text {
  font-size: 12px;
  line-height: 1.5;
  color: #000;
  margin-bottom: 10px;
}

.pet-info {
  font-size: 12px;
  color: #d78d86;
}

/* Q&A Section */
.qa-column h2 {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 20px;
}

.qa-list {
  margin-bottom: 40px;
}

.qa-item {
  margin-bottom: 20px;
}

.question {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 8px;
}

.q-icon {
  width: 19px;
  height: 19px;
  flex-shrink: 0;
}

.question strong {
  font-size: 14px;
  font-weight: 700;
  color: #000;
}

.answer {
  margin-left: 29px;
  font-size: 12px;
  color: #000;
  line-height: 1.5;
}

/* Testimonials Section */
/* (moved to ReviewsQASection component) */

.testimonials-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
}

.testimonial-card {
  background: white;
  border: 2px solid #ffe1cf;
  border-radius: 20px;
  padding: 30px;
  position: relative;
}

.quote-mark {
  position: absolute;
  top: 10px;
  left: 20px;
  font-family: 'Eczar', serif;
  font-size: 80px;
  color: #e37267;
  line-height: 1;
}

.testimonial-text {
  margin: 40px 0 20px;
  font-size: 12px;
  line-height: 1.5;
  color: #000;
}

.testimonial-author {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.testimonial-author img {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  object-fit: cover;
  background: #f0f0f0;
}

.author-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.author-info strong {
  font-size: 12px;
  color: #000;
}

.author-info span {
  font-size: 10px;
  color: #908c8c;
}

.testimonial-stars {
  display: flex;
  gap: 2px;
}

.testimonial-stars .star {
  color: #e37267;
  font-size: 14px;
}

/* CTA Section */
.cta-section {
  background: #fffef9;
  padding: 100px 0;
  position: relative;
}

.cta-container {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 60px;
  align-items: center;
}

.cta-visual {
  display: flex;
  justify-content: flex-end; /* Push image towards center */
  padding-right: 40px;
}

.cta-visual img {
  width: 100%;
  max-width: 500px;
  height: auto;
  display: block;
  opacity: 0.8;
}

.cta-content {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-self: start; /* Align content to the left of its column area */
  padding-left: 20px;
}

.cta-subtitle-small {
  font-family: 'Jost', sans-serif;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  color: #000;
  margin: 0 0 24px 0;
}

.cta-title {
  font-family: 'Eczar', serif;
  font-size: 46px;
  font-weight: 600;
  color: #e37267;
  line-height: 1.4;
  margin: 0;
  text-transform: uppercase;
}

.cta-description {
  font-size: 12px;
  color: #e37267;
  font-weight: 600;
}

/* Responsive Design */
@media (max-width: 1200px) {
  .container {
    padding: 0 40px;
  }
  
  .product-grid {
    gap: 60px;
  }
  
  .process-timeline {
    grid-template-columns: repeat(3, 1fr);
    row-gap: 40px;
  }
  
  .timeline-line {
    display: none;
  }
  
  .items-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .no-background-grid {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 28px;
  }

  .no-background-image-wrapper {
    width: 200px;
    padding: 22px;
  }

  .travel-section {
    padding: 80px 0;
  }
  .travel-container {
    gap: 40px;
  }
}

@media (max-width: 992px) {
  .product-grid {
    grid-template-columns: 1fr;
    gap: 40px;
  }
  
  .gallery-section {
    width: 100%;
    max-width: none;
    margin: 0;
    flex-direction: column; /* stack nav and image */
    align-items: stretch;
  }
  
  .main-image-container {
    width: 100%;
    order: 1; /* show image first */
    max-width: none;
  }
  
  .gallery-nav {
    flex-direction: row; /* thumbnails below image */
    justify-content: flex-start;
    order: 2;
    width: 100%;
    flex: 0 0 auto;
    max-width: 100%;
    max-height: none;
    overflow-x: auto;
    overflow-y: hidden;
  }
  
  .journey-features {
    grid-template-columns: 1fr;
  }
  
  .items-details {
    grid-template-columns: 1fr;
  }
  
  .content-grid {
    grid-template-columns: 1fr;
  }
  
  .testimonials-grid {
    grid-template-columns: 1fr;
  }
  
  /* Stack three-column layout on tablet/mobile */
  .three-col-layout {
    grid-template-columns: 1fr;
    gap: 24px;
  }
  .side-column { order: 2; display: none; }
  .center-media { order: 1; }
  /* Expand card content by default on touch devices */
  .card-body {
    max-height: none;
    opacity: 1;
    padding: 16px 18px;
    transition: none;
  }
  /* Show mobile swiper */
  .mobile-swiper {
    display: block;
    width: 100vw;
    margin-left: calc(50% - 50vw);
    margin-right: calc(50% - 50vw);
    overflow: hidden;
  }
  .mobile-swiper .swiper-track { padding: 8px 20px 14px; }
  /* Reduce center image size on tablet */
  .center-media { align-items: center; }
  .center-image { max-width: 420px; margin: 0 auto; }

  .no-background-section {
    padding: 60px 0;
  }
  .no-background-grid {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 20px;
  }
  .no-background-card {
    gap: 12px;
  }
  .no-background-image-wrapper {
    width: 170px;
    padding: 18px;
  }
  .no-background-name {
    font-size: 11px;
    letter-spacing: 0.08em;
  }

  .travel-section {
    padding: 70px 0;
  }
  .travel-container {
    grid-template-columns: 1fr;
    text-align: center;
  }
  .travel-copy {
    align-items: center;
  }
  .travel-polaroid {
    max-width: 620px;
    margin: 0 auto;
  }
}

@media (max-width: 640px) {
  .container {
    padding: 0 20px;
  }
  
  .gallery-nav {
    flex-direction: row;
    overflow-x: auto;
    width: 100%;
    justify-content: flex-start;
  }
  
  .gallery-thumb {
    width: 72px;
    height: 40px;
  }
  
  .process-timeline {
    grid-template-columns: 1fr;
  }
  
  .cta-title {
    font-size: 32px;
  }
  
  .items-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .thumb { width: 60px; height: 36px; }
  /* Further shrink center image on mobile */
  .center-image { max-width: 300px; }
  .center-media { gap: 12px; }
  .mobile-swiper .swiper-track { padding: 8px 16px 14px; }

  .no-background-section {
    padding: 50px 0;
  }
  .no-background-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }
  .no-background-image-wrapper {
    width: 140px;
    padding: 14px;
  }
  .no-background-index {
    font-size: 14px;
  }
  .no-background-name {
    font-size: 10px;
    letter-spacing: 0.06em;
  }

  .travel-section {
    padding: 60px 0;
  }
  .travel-title {
    font-size: 24px;
  }
  .travel-subtitle {
    font-size: 13px;
  }
  .travel-polaroid {
    max-width: 100%;
    border-radius: 18px;
  }
}

@media (max-width: 992px) {
  .cta-container {
    grid-template-columns: 1fr;
    text-align: center;
  }
  .cta-visual {
    display: none;
  }
  .cta-content {
    padding-left: 0;
    justify-self: center;
  }
}
</style>


